"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Utensils, Cookie, Moon, Box, Luggage, ShieldCheck, Sparkles, Layers, Droplets, Camera, Navigation, Scissors, Smile, Pill, Bug, Cat, Cpu, ArrowUpRight, Stethoscope, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const categories = [{
  id: "food",
  name: "Food",
  description: "Dry kibble, wet formulas, raw food, and prescription diets vetted for nutritional adequacy.",
  icon: Utensils,
  count: "42 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "treats",
  name: "Treats",
  description: "Training rewards, dental chews, single-ingredient bites, and low-calorie options.",
  icon: Cookie,
  count: "28 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "beds",
  name: "Beds",
  description: "Orthopedic memory foam, calming bolsters, elevated cots, and washable loungers.",
  icon: Moon,
  count: "19 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "crates",
  name: "Crates",
  description: "Heavy-duty wire kennels, furniture-style crates, and escape-proof travel units.",
  icon: Box,
  count: "14 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "carriers",
  name: "Carriers",
  description: "Airline-compliant soft carriers, backpack totes, and crash-tested vehicle carriers.",
  icon: Luggage,
  count: "16 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "harnesses",
  name: "Harnesses",
  description: "No-pull front-clip harnesses, tactical escape-free vests, and step-in walking gear.",
  icon: ShieldCheck,
  count: "22 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "toys",
  name: "Toys",
  description: "Mental enrichment puzzles, durable chew toys, plush fetchers, and interactive lasers.",
  icon: Sparkles,
  count: "35 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "feeders",
  name: "Feeders",
  description: "Slow-feed bowls, microchip-activated dispensers, and timed automatic feeders.",
  icon: Layers,
  count: "18 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "water-fountains",
  name: "Water Fountains",
  description: "Stainless steel and ceramic drinking fountains with multi-stage filtration systems.",
  icon: Droplets,
  count: "15 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "pet-cameras",
  name: "Pet Cameras",
  description: "HD two-way audio monitors, treat-tossing cameras, and night-vision room scanners.",
  icon: Camera,
  count: "12 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "gps-trackers",
  name: "GPS Trackers",
  description: "Cellular collars, Bluetooth tags, and live geofencing location devices.",
  icon: Navigation,
  count: "11 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "grooming",
  name: "Grooming",
  description: "Deshedding tools, low-noise clippers, hypoallergenic shampoos, and nail grinders.",
  icon: Scissors,
  count: "26 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "dental-care",
  name: "Dental Care",
  description: "Enzymatic toothpastes, dual-ended brushes, dental water additives, and plaque wipes.",
  icon: Smile,
  count: "17 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "supplements",
  name: "Supplements",
  description: "Joint support formulas, omega-3 oils, probiotics, and calming chews with clear dosages.",
  icon: Pill,
  count: "31 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "flea-tick",
  name: "Flea & Tick",
  description: "Topical treatments, preventive collars, and oral preventive comparison breakdowns.",
  icon: Bug,
  count: "20 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "litter-boxes",
  name: "Litter Boxes",
  description: "Self-cleaning automated units, top-entry boxes, non-tracking litter, and odor filters.",
  icon: Cat,
  count: "19 guides",
  to: "/ProductsReviewsPage"
}, {
  id: "pet-technology",
  name: "Pet Technology",
  description: "Smart doors, automated litter monitors, diagnostic health collars, and sensor gear.",
  icon: Cpu,
  count: "14 guides",
  to: "/ProductsReviewsPage"
}];
export default function Categories() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.04
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="intro-categories" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase mb-4">
            <CheckCircle2 className="w-3.5 h-3.5 text-foreground" />
            Independent Product Directory
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground leading-tight">
            Products We Help You Choose
          </h2>

          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Every product guide is vetted against clinical safety criteria and tested in real homes with dogs and cats before we recommend it. We accept no paid placements or sponsored verdicts.
          </p>

          {/* Clinical Governance Callout */}
          <div className="mt-6 rounded-lg bg-card border border-border border-l-4 border-l-foreground p-5 sm:p-6 shadow-xs">
            <div className="flex items-start gap-3.5">
              <div className="p-2 rounded-md bg-secondary text-foreground shrink-0 mt-0.5">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <h3 className="font-sans text-sm font-semibold text-foreground uppercase tracking-wide">
                  Clinical Safety &amp; Testing Standards
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our veterinary review board examines materials, toxicity profiles, mechanical failure points, and nutritional claims. If an item presents a choking risk, unverified chemical additives, or false health claims, we remove it from consideration.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 17-Item Category Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {categories.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} variants={itemVariants} className="h-full">
                <Link to={item.to} className="group relative flex flex-col justify-between h-full p-5 sm:p-6 rounded-xl bg-card border border-border transition-[background-color,border-color,box-shadow] duration-250 hover:bg-card/80 hover:border-border/80 hover:shadow-md">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-secondary text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                        {item.count}
                      </span>
                    </div>

                    <h3 className="font-serif text-lg sm:text-xl font-semibold text-foreground mb-2 flex items-center gap-1.5">
                      {item.name}
                      <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 text-muted-foreground transition-[opacity,transform] duration-200" />
                    </h3>

                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-3">
                      {item.description}
                    </p>
                  </div>

                  <div className="mt-5 pt-3.5 border-t border-border/60 flex items-center justify-between text-xs font-medium text-foreground">
                    <span>Browse category</span>
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                      View all &rarr;
                    </span>
                  </div>
                </Link>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Hub Summary Action */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-border">
          <div className="space-y-1.5 max-w-2xl">
            <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground">
              Looking for our latest head-to-head laboratory tests?
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Read our full methodology covering testing protocols, veterinary panel selection, and how we purchase every product at retail price.
            </p>
          </div>

          <Button asChild size="lg" className="w-full sm:w-auto shrink-0 shadow-xs">
            <Link to="/AboutPetsWhirlPage">
              Read testing methodology
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}